import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import VideoGrid from '../components/Videos/VideoGrid';
import Pagination from '../components/Common/Pagination';
import { useCart } from '../context/CartContext';
import { useDeviceAuth } from '../context/DeviceAuthContext';
import { useLocalVideos } from '../hooks/useLocalVideos';
import { is22InchPortrait, setupPortraitViewport } from '../utils/portraitModeDetector';
import imageConfig from '../config/images';
import '../styles/VideoSelectionPage.css';

// Number of videos to display per page
const VIDEOS_PER_PAGE = 15;

const VideoSelectionPage: React.FC = () => {
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { addToCart, removeFromCart, isInCart, clearCart } = useCart();
  const { isDeviceAuthorized } = useDeviceAuth();
  const history = useHistory();
  const [manualFolderSelection, setManualFolderSelection] = useState<boolean>(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout>();
  const INACTIVITY_TIMEOUT = 60000; // 60 seconds
  const MAX_VIDEOS_ALLOWED = 6; // Maximum number of videos that can be selected
  const [showMaxVideosMessage, setShowMaxVideosMessage] = useState<boolean>(false);
  
  // Add ref for video grid
  const videoGridRef = useRef<HTMLDivElement>(null);
  
  // Setup portrait viewport fix
  useEffect(() => {
    return setupPortraitViewport();
  }, []);
  
  const { 
    videos, 
    loading, 
    error, 
    selectClipoFolder, 
    lastFolderName,
    hasSelectedFolder 
  } = useLocalVideos();

  // Automatically try to load videos when the component mounts, 
  // but only if manual selection hasn't been tried yet
  useEffect(() => {
    const attemptAutoLoad = async () => {
      if (!loading && !hasSelectedFolder && !manualFolderSelection) {
        console.log("Auto-attempting to load videos from known directory handle");
        // Set a flag to show we're trying manual selection if this fails
        setManualFolderSelection(true);
        try {
          await selectClipoFolder(true);
        } catch (err) {
          console.error("Error auto-loading videos:", err);
        }
      }
    };
    
    attemptAutoLoad();
  }, [loading, hasSelectedFolder, manualFolderSelection, selectClipoFolder]);

  // Sort videos by date modified (createdAt) in descending order
  const sortedVideos = useMemo(() => {
    if (!videos || videos.length === 0) return [];
    
    // Get current date and strip time to compare only the date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Filter videos to only show those created today
    const todayVideos = videos.filter(video => {
      const createdDate = new Date(video.createdAt || 0);
      createdDate.setHours(0, 0, 0, 0);
      return createdDate.getTime() === today.getTime();
    });
    
    // Create a copy of filtered videos and sort by date (most recent first)
    return [...todayVideos].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
  }, [videos]);

  // Calculate total pages based on currently loaded videos
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedVideos.length / VIDEOS_PER_PAGE));
  }, [sortedVideos]);

  // Get current page of videos
  const currentVideos = useMemo(() => {
    const startIndex = (currentPage - 1) * VIDEOS_PER_PAGE;
    const endIndex = startIndex + VIDEOS_PER_PAGE;
    return sortedVideos.slice(startIndex, endIndex);
  }, [sortedVideos, currentPage]);

  // Check if thumbnails are still loading (using default thumbnails)
  const thumbnailsLoading = useMemo(() => {
    return loading || currentVideos.some(video => !video.thumbnailUrl || video.thumbnailUrl.includes('default-thumbnail'));
  }, [currentVideos, loading]);

  // Adjust current page if it's out of bounds
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [totalPages, currentPage]);

  const handlePageChange = (page: number) => {
    // Allow page changes if within valid range
    if (page >= 1 && page <= Math.max(1, totalPages)) {
      setCurrentPage(page);
      // Scroll to top of the page when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!isDeviceAuthorized) {
      history.push('/box');
    }
  }, [isDeviceAuthorized, history]);

  const toggleVideoSelection = (videoId: string) => {
    // If already in cart, always allow removal
    if (isInCart(videoId)) {
      removeFromCart(videoId);
    } else {
      // Check if adding this video would exceed the limit
      const currentSelectedCount = sortedVideos.filter(v => isInCart(v.id)).length;
      
      if (currentSelectedCount >= MAX_VIDEOS_ALLOWED) {
        // Show the max videos message
        setShowMaxVideosMessage(true);
        // Don't add the video
        return;
      }
      
      // Add the video if under the limit
      addToCart(videoId);
    }
  };

  const handleVideoClick = (videoId: string) => {
    if (playingVideo === videoId) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
    }
  };

  const handleCheckout = () => {
    // Make sure videos are cached before going to checkout
    if (videos.length > 0) {
      console.log("Saving videos to cache before checkout");
      try {
        // Save to localStorage with reduced data to avoid size limits
        const simplifiedVideos = videos.map(video => ({
          id: video.id,
          title: video.title,
          thumbnailUrl: video.thumbnailUrl,
          url: video.url,
          duration: video.duration,
          createdAt: video.createdAt,
          userId: video.userId
        }));
        
        // Store in multiple storage mechanisms for redundancy
        localStorage.setItem('clipo-video-cache', JSON.stringify(simplifiedVideos));
        
        // Also store selected video IDs separately
        const selectedIds = sortedVideos
          .filter(v => isInCart(v.id))
          .map(v => v.id);
        localStorage.setItem('clipo-selected-videos', JSON.stringify(selectedIds));
      } catch (err) {
        console.error("Error saving videos to cache:", err);
      }
    }
    
    // Set flag that we are going to checkout page
    sessionStorage.setItem('going-to-checkout', 'true');
    history.push('/box/checkout');
  };

  // Add detection of the infinite loading cycle
  useEffect(() => {
    if (loading) {
      // If we're in a loading state, track the number of initialization attempts
      const cycleCount = parseInt(sessionStorage.getItem('loading-cycle-count') || '0', 10);
      sessionStorage.setItem('loading-cycle-count', (cycleCount + 1).toString());
      
      console.log(`Loading cycle detected: ${cycleCount + 1}`);
      
      // If we've been through more than 5 cycles, something is wrong - force reload
      if (cycleCount >= 3) {
        console.warn('⚠️ Detected infinite loading cycle, forcing page reload');
        sessionStorage.removeItem('loading-cycle-count');
        window.location.reload();
      }
    } else {
      // Reset the cycle count when loading ends
      sessionStorage.removeItem('loading-cycle-count');
    }
  }, [loading]);

  const handleSelectFolder = async () => {
    try {
      // Reset any cycle detection
      sessionStorage.removeItem('loading-cycle-count');
      sessionStorage.removeItem('has-attempted-restore');
      sessionStorage.removeItem('initialized-videos');
      sessionStorage.removeItem('selecting-folder');
      
      console.log("Starting folder selection from admin button...");
      
      // Set the force directory picker flag before calling selectClipoFolder
      sessionStorage.setItem('force-directory-picker', 'true');
      
      // Call selectClipoFolder - the useStoredOnly parameter doesn't matter
      // since our flag will bypass that check in the hook
      await selectClipoFolder(false);
      
      // Remove the flag after use
      sessionStorage.removeItem('force-directory-picker');
    } catch (err) {
      console.error("Error in handleSelectFolder:", err);
      // Clean up flag even on error
      sessionStorage.removeItem('force-directory-picker');
    }
    
    // Add a fallback timeout to ensure loading is cleared even if there's an issue
    setTimeout(() => {
      if (loading) {
        console.warn("Loading state was still true after folder selection, forcing it to false");
        // Can't directly access hook's setLoading function, but we can reload the page
        window.location.reload();
      }
    }, 15000); // 15 second safety timeout
  };

  // Function to reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      console.log('No activity detected for 60 seconds, redirecting to homepage...');
      history.push('/box');
    }, INACTIVITY_TIMEOUT);
  }, [history]);

  // Set up event listeners for user activity
  useEffect(() => {
    // Initial timer setup
    resetInactivityTimer();

    // Add event listeners for various user activities
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    // Cleanup function
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [resetInactivityTimer]);

  // Effect for the max videos message
  useEffect(() => {
    // Hide the max videos message after a short delay
    if (showMaxVideosMessage) {
      const timer = setTimeout(() => {
        setShowMaxVideosMessage(false);
      }, 3000); // Hide after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showMaxVideosMessage]);

  // Add some debugging in the loading section
  if (loading) {
    console.log("App is in loading state - Hook state:", { videos, error, hasSelectedFolder });
    
    // Force fixed size and full width for the loading background, especially for 22-inch portrait displays
    const loadingBackgroundStyle = {
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${imageConfig.backgroundImage || imageConfig.defaultBackgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    };

    // Check if this is a 22-inch portrait display
    if (is22InchPortrait()) {
      // Override styles completely for 22-inch portrait
      return (
        <>
          <style>
            {`
              @keyframes portraitSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              .portrait-spinner {
                display: inline-block;
                width: 50px;
                height: 50px;
                border: 4px solid rgba(2, 81, 224, 0.2);
                border-left-color: #0251E0;
                border-radius: 50%;
                animation: portraitSpin 1s linear infinite;
              }
            `}
          </style>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '1080px',
              height: '1920px',
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${imageConfig.backgroundImage || imageConfig.defaultBackgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              overflow: 'hidden'
            }}
          >
            <div 
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: '320px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                textAlign: 'center'
              }}
            >
              <div style={{ marginBottom: '24px' }}>
                <div className="portrait-spinner"></div>
              </div>
              <h3 style={{ color: '#333', fontSize: '20px', marginBottom: '16px' }}>Preparing Your Videos</h3>
              <p style={{ color: '#666', fontSize: '14px' }}>This will take just a moment</p>
              {error && <p style={{ color: 'red', marginTop: '16px' }}>Error: {error}</p>}
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="loading-container" style={loadingBackgroundStyle}>
        <div className="loading-content">
          <div className="loading-animation">
            <div className="spinner-ring"></div>
          </div>
          <h3 className="loading-title">Preparing Your Videos</h3>
          <p className="loading-subtitle">This will take just a moment</p>
          {error && <p className="loading-error">Error: {error}</p>}
        </div>
      </div>
    );
  }

  const backgroundStyle = hasSelectedFolder && videos.length > 0 
    ? {
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.7)), url(${imageConfig.backgroundImage || imageConfig.defaultBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'fixed' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      } 
    : {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${imageConfig.backgroundImage || imageConfig.defaultBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      };

  const titleClass = hasSelectedFolder && videos.length > 0 
    ? "page-title dark-bg-title" 
    : "page-title";
  
  // Add a class for the container to adjust spacing based on state
  const containerClass = hasSelectedFolder
    ? "video-selection-page has-selected-folder"
    : "video-selection-page";

  return (
    <div className="video-selection-page-container" style={backgroundStyle}>
      <div className={containerClass}>
        {/* Home button in top left */}
        <button 
          className="home-button" 
          onClick={() => history.push('/box')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          Home
        </button>
        
        {/* Max videos message - shown when limit is reached */}
        {showMaxVideosMessage && (
          <div className="max-videos-message">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            Maximum of {MAX_VIDEOS_ALLOWED} videos can be selected
          </div>
        )}
        
        {/* Admin button in top right */}
        {hasSelectedFolder && (
          <button 
            className="admin-button" 
            onClick={handleSelectFolder}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            Admin
          </button>
        )}

        {/* Folder selection UI animates in from bottom */}
        <div className="folder-selection-container">
          {hasSelectedFolder && lastFolderName ? (
            <div></div>
          ) : (
            <div className="folder-selection-centered">
              <h2 className="folder-selection-title">Select Your Videos</h2>
              
              <div className="folder-selection-instructions">
                <ol className="instruction-list">
                  <li>
                    <span className="instruction-number">1</span>
                    <span className="instruction-text">Click the button below</span>
                  </li>
                  <li>
                    <span className="instruction-number">2</span>
                    <span className="instruction-text">Go to Clipo folder and click "Select"</span>
                  </li>
                  <li>
                    <span className="instruction-number">3</span>
                    <span className="instruction-text">Click "Allow" if asked for permissions</span>
                  </li>
                </ol>
              </div>
              
              <button 
                className="select-folder-button-large" 
                onClick={handleSelectFolder}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                </svg>
                Browse Videos
              </button>
            </div>
          )}
          {!hasSelectedFolder && !lastFolderName && (
            <p className="help-text">Please call our staff for assistance</p>
          )}
        </div>

        {/* Title animates in from bottom */}
        {hasSelectedFolder && (
          <h1 className={titleClass}>Your Clipo Videos</h1>
        )}
        
        
        {/* Error message animates in from left */}
        {error && (
          <div className="error-message">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}
        
        {/* Content area with videos */}
        {hasSelectedFolder ? (
          sortedVideos.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
                  <line x1="7" y1="2" x2="7" y2="22"></line>
                  <line x1="17" y1="2" x2="17" y2="22"></line>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                </svg>
              </div>
              <h3>No Videos Found</h3>
              <p>The selected folder doesn't contain any video files</p>
            </div>
          ) :
            <>
              {/* Video modal with watermark overlay */}
              {playingVideo && (
                <div className="video-modal" onClick={() => setPlayingVideo(null)}>
                  <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="close-button" onClick={() => setPlayingVideo(null)}>
                      ✕
                    </button>
                    <video
                      className="modal-video-player"
                      src={sortedVideos.find(v => v.id === playingVideo)?.url}
                      controls
                      autoPlay
                      playsInline
                    />
                    <div className="watermark-overlay">
                      <img 
                        src={imageConfig.watermarkImage || imageConfig.defaultWatermarkImage} 
                        alt="Watermark" 
                        className="watermark-image" 
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Display pagination info and controls above the grid */}
              {(sortedVideos.length > VIDEOS_PER_PAGE || loading) && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    {thumbnailsLoading && (
                      <div className="loading-indicator-banner">
                        <div className="loading-spinner-mini"></div>
                        <span>Loading video...</span>
                      </div>
                    )}
                    <p>
                      {videos.length === 0 && loading ? (
                        "Loading videos..."
                      ) : (
                        <>
                          Showing {(currentPage - 1) * VIDEOS_PER_PAGE + 1} - {Math.min(currentPage * VIDEOS_PER_PAGE, sortedVideos.length)} of {sortedVideos.length} videos
                        </>
                      )}
                    </p>
                  </div>
                  
                  <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}

              {/* Video grid with older videos divider for portrait displays */}
              {videos.length === 0 && loading ? (
                <div className="video-grid-container">
                  <div className="video-grid-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading videos...</p>
                  </div>
                </div>
              ) : (
                <div className="video-grid-container">
                  {loading && (
                    <div className="video-grid-floating-indicator">
                      <div className="loading-spinner-small"></div>
                      <span>Loading videos...</span>
                    </div>
                  )}
                  
                  {/* Recent Videos divider */}
                  {sortedVideos.length > 0 && (
                    <div className="video-section-divider recent-videos">
                      <div className="divider-line"></div>
                      <div className="divider-label">Recent Videos</div>
                      <div className="divider-line"></div>
                    </div>
                  )}
                  
                  {/* For portrait 22-inch display, we need to render the first 9 videos,
                      then the divider, then the rest of the videos */}
                  {currentPage === 1 && sortedVideos.length > 9 ? (
                    <>
                      {/* First 9 videos */}
                      <div 
                        ref={videoGridRef} 
                        className="video-grid-first-section"
                        style={{ padding: 0, margin: 0 }}
                      >
                        <VideoGrid 
                          videos={currentVideos.slice(0, 9)} 
                          onVideoSelect={toggleVideoSelection}
                          selectedVideos={sortedVideos.filter(v => isInCart(v.id)).map(v => v.id)}
                          onVideoClick={handleVideoClick}
                          playingVideo={playingVideo}
                        />
                      </div>
                      
                      {/* Older Videos divider after 9 videos */}
                      <div className="video-section-divider older-videos">
                        <div className="divider-line"></div>
                        <div className="divider-label">Older Videos</div>
                        <div className="divider-line"></div>
                      </div>
                      
                      {/* Remaining videos */}
                      <div 
                        className="video-grid-second-section"
                        style={{ padding: 0, margin: 0 }}
                      >
                        <VideoGrid 
                          videos={currentVideos.slice(9)} 
                          onVideoSelect={toggleVideoSelection}
                          selectedVideos={sortedVideos.filter(v => isInCart(v.id)).map(v => v.id)}
                          onVideoClick={handleVideoClick}
                          playingVideo={playingVideo}
                        />
                      </div>
                    </>
                  ) : (
                    /* If we're not on the first page or don't have enough videos, just show normally */
                    <div ref={videoGridRef}>
                      <VideoGrid 
                        videos={currentVideos} 
                        onVideoSelect={toggleVideoSelection}
                        selectedVideos={sortedVideos.filter(v => isInCart(v.id)).map(v => v.id)}
                        onVideoClick={handleVideoClick}
                        playingVideo={playingVideo}
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Fixed checkout controls at bottom - only show when videos are selected */}
              {sortedVideos.filter(v => isInCart(v.id)).length > 0 && (
                <div className="checkout-controls visible">
                  <button 
                    className="checkout-button"
                    onClick={handleCheckout}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1"></circle>
                      <circle cx="20" cy="21" r="1"></circle>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                    </svg>
                    Checkout
                  </button>
                  <div 
                    className="selected-videos-counter"
                    onClick={clearCart}
                  >
                    Clear cart ({sortedVideos.filter(v => isInCart(v.id)).length} {sortedVideos.filter(v => isInCart(v.id)).length !== 1 ? 'videos' : 'video'})
                  </div>
                </div>
              )}
            </>
        ) : null}
      </div>
    </div>
  );
};

export default VideoSelectionPage;